import type { CloudImageTiers } from "./cloudImage";

// The configuration tool keys every config entry by `name`; the Cloudimage tiers live on the
// shared "global" entry, under `configuration.cloudImageTiers`.
const APPLICATION_ID = "foodiweb";
const GLOBAL_CONFIG_NAME = "global";

// Coerce the config-tool tier values (which may arrive as numbers or numeric strings) into a
// CloudImageTiers shape, dropping anything that is not a positive number.
const toTiers = (raw: { [key: string]: unknown }): CloudImageTiers => {
  const tiers: CloudImageTiers = {};
  (["low", "medium", "high"] as const).forEach((key) => {
    const value = Number(raw?.[key]);
    if (Number.isFinite(value) && value > 0) {
      tiers[key] = value;
    }
  });
  return tiers;
};

// Fetch the "global" entry from the configuration tool (a DynamoDB exposed via
// CONFIGURATION_TOOL_URL) and return its `configuration.cloudImageTiers`. Returns null on any
// failure (network / parsing / missing entry) so callers keep the hardcoded defaults. Pure: it
// only reads config and fetches — the redux dispatch lives in CloudImageTiersThunks.
export const fetchCloudImageTiers = async (): Promise<CloudImageTiers | null> => {
  const { CONFIGURATION_TOOL_URL, CONFIGURATION_TOOL_API_KEY } = window.config;
  if (!CONFIGURATION_TOOL_URL || !CONFIGURATION_TOOL_API_KEY) {
    return null;
  }

  try {
    const response = await window.fetch(
      `${CONFIGURATION_TOOL_URL}/conf?application_id=${APPLICATION_ID}`,
      { method: "GET", headers: { "x-api-key": CONFIGURATION_TOOL_API_KEY } }
    );
    // Response shape mirrors the brand-theme endpoint: { data: { data: [{ name, configuration }] } }.
    const { data } = await response.json();
    const configs: Array<{
      name?: string;
      configuration?: { cloudImageTiers?: object };
    }> = data?.data ?? [];
    const cloudImageTiers = configs.find((c) => c?.name === GLOBAL_CONFIG_NAME)
      ?.configuration?.cloudImageTiers;

    return cloudImageTiers
      ? toTiers(cloudImageTiers as { [key: string]: unknown })
      : null;
  } catch {
    // Keep the hardcoded defaults on any network / parsing error.
    return null;
  }
};
